<template>
  <div
    v-if="visible && position !== 'popup'"
    class="filing-instructions-container"
    :class="{
      [`filing-instructions-${position}`]: position !== 'popup',
    }"
  >
    <div class="panel">
      <div
        class="resize-handle"
        :class="{
          [`resize-handle-${position}`]: position !== 'popup',
        }"
      >
        <v-icon>drag_handle</v-icon>
      </div>

      <div class="title-bar" :class="{ 'pt-1': position === 'bottom' }">
        <div class="px-3">
          <sc-h4>
            Filing Instructions
          </sc-h4>
        </div>

        <v-layout class="py-3" v-if="isAnalyzing" align-center justify-center>
          <span class="grey--text">
            Analyzing filing instructions...
          </span>
        </v-layout>

        <!-- Position Control Menu -->
        <div class="position-controls">
          <v-menu content-class="position-menu">
            <v-btn icon class="icon-btn" slot="activator">
              <img
                v-if="currentPosition"
                :src="currentPosition.icon"
                width="20"
              />

              <v-icon v-else>more_vert</v-icon>
            </v-btn>

            <v-list>
              <v-list-tile
                v-for="pos in positions"
                :key="pos.value"
                @click="$emit('update:position', pos.value)"
                :disabled="pos.value === position"
              >
                <v-list-tile-title>
                  <v-layout justify-start align-center>
                    <img :src="pos.icon" width="20" class="mr-3" />
                    <span>
                      {{ pos.title }}
                    </span>
                  </v-layout>
                </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>

          <v-tooltip bottom>
            <v-btn icon class="icon-btn" @click="closePanel" slot="activator">
              <v-icon>close</v-icon>
            </v-btn>
            <span>
              Close
            </span>
          </v-tooltip>
        </div>
      </div>

      <v-layout class="py-3" v-if="!pageImages" align-center justify-center>
        <sc-small> Loading filing instructions... </sc-small>
      </v-layout>

      <div
        v-else-if="
          taxReturn.filing_instructions &&
            taxReturn.filing_instructions.length > 0
        "
      >
        <v-layout column align-center>
          <div
            v-for="{ page } in taxReturn.filing_instructions"
            :key="page"
            class="page-container"
          >
            <div
              v-if="pageImages.find(i => i.page === page)"
              class="page-wrapper"
            >
              <img
                :src="pageImages.find(i => i.page === page).thumbnail"
                class="page"
                :class="{
                  [`page-${page}`]: true,
                }"
              />
              <div class="bounding-boxes-overlay">
                <v-tooltip
                  v-for="(block, blockIndex) in getBlocksByPage(page)"
                  :disabled="!showTooltip"
                  :key="blockIndex"
                  bottom
                >
                  <div
                    slot="activator"
                    class="bounding-box"
                    :class="{
                      highlighted: highlightedBlockNumbers.includes(
                        block.blockNumber,
                      ),
                      [`block-${block.blockNumber}`]: true,
                    }"
                    :style="getBoundingBoxStyle(block.boundingBox)"
                  />
                  <span>{{ block.blockNumber }}</span>
                </v-tooltip>
              </div>
            </div>
          </div>
        </v-layout>
      </div>

      <v-layout v-else class="py-3" align-center justify-center>
        <sc-small> No filing instructions to show. </sc-small>
      </v-layout>
    </div>
  </div>
</template>

<script>
import interact from 'interactjs';
import API from '@/api';
import { generatePdfThumbnails } from '@/utils/pdf';
import { PDFDocument } from 'pdf-lib';
import { filingInstructionsStorage } from '@/utils/filingInstructionsStorage';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import isEmpty from 'lodash/isEmpty';

export default {
  props: {
    taxReturn: {
      type: Object,
    },
    position: {
      type: String,
      default: 'right',
      validator: value => ['left', 'right', 'bottom'].includes(value),
    },
    visible: {
      type: Boolean,
      default: false,
    },
    highlightedBlockNumbers: {
      type: Array,
      default: () => [],
    },
  },

  mixins: [FirmPreferenceMixins],

  data() {
    return {
      pageImages: null,
      filingInstructionsBlocks: [],
      isAnalyzing: false,

      positions: [
        {
          title: 'Dock to bottom',
          value: 'bottom',
          icon: require('@/assets/icons/dock-bottom.svg'),
        },
        {
          title: 'Dock to left',
          value: 'left',
          icon: require('@/assets/icons/dock-left.svg'),
        },
        {
          title: 'Dock to right',
          value: 'right',
          icon: require('@/assets/icons/dock-right.svg'),
        },
        {
          title: 'Show in a separate window',
          value: 'popup',
          icon: require('@/assets/icons/dock-window.svg'),
        },
      ],

      // For debugging use only
      showTooltip: false,
    };
  },

  computed: {
    currentPosition() {
      return this.positions.find(p => p.value === this.position);
    },
  },

  methods: {
    async loadPageImages() {
      const response = await API.integration.downloadDocument({
        companyId: this.taxReturn.company_id,
        link: this.taxReturn.original_file,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });
      this.pageImages = await generatePdfThumbnails(pdfData, 1000);
    },

    async loadFilingInstructionsAnalysis() {
      try {
        this.isAnalyzing = true;
        const response = await API.taxReturn.analyzeFilingInstructions({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
          pages: this.taxReturn.filing_instructions.map(fi => fi.page),
        });
        this.filingInstructionsBlocks = response.data.data || [];
      } catch (error) {
        console.error('Error loading filing instructions analysis:', error);
        this.filingInstructionsBlocks = [];
      } finally {
        this.isAnalyzing = false;
      }
    },

    getBlocksByPage(page) {
      const blocks = this.filingInstructionsBlocks.filter(
        block => block.page === page,
      );
      return blocks;
    },

    getBoundingBoxStyle(boundingBox) {
      if (!boundingBox) return {};

      // Convert normalized coordinates to percentages
      return {
        left: `${boundingBox.left * 100}%`,
        top: `${boundingBox.top * 100}%`,
        width: `${boundingBox.width * 100}%`,
        height: `${boundingBox.height * 100}%`,
      };
    },

    closePanel() {
      this.$emit('update:visible', false);
    },

    scrollToBlockWithHighlights() {
      if (this.highlightedBlockNumbers.length === 0) {
        return;
      }

      // Get the smallest block number to scroll to
      const smallestBlockNumber = Math.min(...this.highlightedBlockNumbers);

      // Scroll to the block element
      this.$nextTick(() => {
        const blockElement = this.$el.querySelector(
          `.block-${smallestBlockNumber}`,
        );
        if (blockElement) {
          blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    setupResizeListener() {
      const filingInstructionsPanel = this.$el;

      if (filingInstructionsPanel) {
        // Load saved panel size
        const savedSize = filingInstructionsStorage.getPanelSize(this.position);
        if (this.position === 'bottom') {
          filingInstructionsPanel.style.height = savedSize + 'px';
        } else {
          filingInstructionsPanel.style.width = savedSize + 'px';
        }

        interact(filingInstructionsPanel).resizable({
          edges: this.getResizeEdges(),
          listeners: {
            move: event => {
              if (this.position === 'bottom') {
                event.target.style.height = event.rect.height + 'px';
              } else {
                event.target.style.width = event.rect.width + 'px';
              }
            },
            end: event => {
              // Save the new size when resize ends
              if (this.position === 'bottom') {
                filingInstructionsStorage.savePanelSize(
                  this.position,
                  event.rect.height,
                );
              } else {
                filingInstructionsStorage.savePanelSize(
                  this.position,
                  event.rect.width,
                );
              }
            },
          },
        });
      }
    },

    getResizeEdges() {
      switch (this.position) {
        case 'left':
          return { right: true };
        case 'right':
          return { left: true };
        case 'bottom':
        default:
          return { top: true };
      }
    },
  },

  watch: {
    position() {
      // Remove old listener
      const filingInstructionsPanel = this.$el;

      if (filingInstructionsPanel) {
        interact(filingInstructionsPanel).unset();
      }

      if (filingInstructionsPanel) {
        // Set up new listener with updated edges
        this.$nextTick(() => {
          this.setupResizeListener();
        });
      }
    },

    visible(newVal) {
      if (newVal) {
        // Panel is now visible, set up the listener
        this.$nextTick(() => {
          this.setupResizeListener();
        });
      } else {
        // Panel is now hidden, clean up the listener
        const filingInstructionsPanel = this.$el;
        if (filingInstructionsPanel) {
          interact(filingInstructionsPanel).unset();
        }
      }
    },

    async highlightedBlockNumbers() {
      if (
        isEmpty(this.filingInstructionsBlocks) &&
        this.taxReturnUseFilingInstructionsParser
      ) {
        await this.loadFilingInstructionsAnalysis();
      }

      this.scrollToBlockWithHighlights();
    },
  },

  async created() {
    await this.loadPageImages();
  },

  async mounted() {
    this.setupResizeListener();

    if (this.taxReturnUseFilingInstructionsParser) {
      await this.loadFilingInstructionsAnalysis();
    }
  },

  beforeDestroy() {
    this.$emit('update:highlightedBlockNumbers', []);
  },
};
</script>

<style lang="css" scoped>
.panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.resize-handle {
  position: absolute;
  user-select: none;
  z-index: 10;
  background: #ddd;
  display: flex;
  justify-content: center;
  align-items: center;

  & >>> .v-icon {
    color: #aaa;
  }

  &.resize-handle-bottom {
    left: 0;
    right: 0;
    height: 8px;
    top: 0;
    cursor: ns-resize;
  }

  &.resize-handle-left {
    width: 8px;
    height: 100%;
    right: 0;
    top: 0;
    cursor: ew-resize;

    & >>> .v-icon {
      transform: rotate(90deg);
    }
  }

  &.resize-handle-right {
    width: 8px;
    height: 100%;
    left: 0;
    top: 0;
    cursor: ew-resize;

    & >>> .v-icon {
      transform: rotate(90deg);
    }
  }
}

.resize-handle:hover {
  background: #ccc;

  & >>> .v-icon {
  }
}

.title-bar {
  height: 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
  background-color: #eee;
}

.position-controls {
  display: flex;
  padding: 8px;
  justify-content: flex-end;
}

.page-container {
  position: relative;
  display: inline-block;
}

.page-wrapper {
  position: relative;
  display: inline-block;
  max-width: 1000px;
}

.page {
  width: 100%;
  height: 100%;
  display: block;
}

.bounding-boxes-overlay {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
}

.bounding-box {
  position: absolute;

  &.highlighted {
    border-color: rgba(255, 193, 7, 1);
    background-color: rgba(255, 193, 7, 0.3);
    box-shadow: 0 0 8px rgba(255, 193, 7, 0.6);
  }
}

.icon-btn {
  margin: 6px 0px;
}

.position-menu {
  & >>> .v-list__tile--disabled img {
    opacity: 0.25;
  }
}

.filing-instructions-container {
  background: #eee;
  position: relative;

  &.filing-instructions-bottom {
    width: 100%;
    flex: none;
  }

  &.filing-instructions-left {
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: auto;
  }

  &.filing-instructions-right {
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: auto;
  }
}
</style>
